import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { MarkdownText } from "../components/MarkdownText";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { ReadingAlerts } from "../components/ReadingAlerts";
import { toast } from "sonner";
import { Sparkles, LayoutGrid, Hash, Trash2 } from "lucide-react";

const DECK_LABELS = { "vera-sibilla": "Sibille", piacentine: "Piacentine", napoletane: "Napoletane", baraja: "Baraja Española", iching: "I Ching", oracolo: "L'Oracolo del Destino", stesa26: "Stesa 26" };
const SPREAD_LABELS = {
  una: "Una carta · Sì/No",
  tre: "Tre carte · Passato/Presente/Futuro",
  croce: "Croce · 4 carte",
  settenaria: "Settenaria · 7 carte",
  tempistiche: "Tempistiche · 15 carte",
  esagramma: "I Ching · Esagramma",
  oracolo: "Il responso",
  stesa26: "Quadro 5×5 + Fuori quadro",
  3: "3 carte",
  5: "5 carte",
};

export default function MieLetture() {
  const [readings, setReadings] = useState([]);
  const [geomancies, setGeomancies] = useState([]);
  const [numeros, setNumeros] = useState([]);

  const load = useCallback(() => {
    api.get("/readings/mine").then((r) => setReadings(r.data)).catch(() => setReadings([]));
    api.get("/geomancy/sessions/mine").then((r) => setGeomancies(r.data)).catch(() => setGeomancies([]));
    api.get("/numerology/archive").then((r) => setNumeros(r.data)).catch(() => setNumeros([]));
  }, []);

  useEffect(() => load(), [load]);

  const deleteReading = async (id) => {
    if (!window.confirm("Vuoi davvero eliminare questa lettura? L'operazione è definitiva.")) return;
    try {
      await api.delete(`/readings/${id}`);
      toast.success("Lettura eliminata.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteGeomancy = async (id) => {
    if (!window.confirm("Vuoi davvero eliminare questa generazione geomantica? L'operazione è definitiva.")) return;
    try {
      await api.delete(`/geomancy/sessions/${id}`);
      toast.success("Generazione eliminata.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteNumerology = async (id) => {
    if (!window.confirm("Vuoi davvero eliminare questa numerologia? L'operazione è definitiva.")) return;
    try {
      await api.delete(`/numerology/archive/${id}`);
      toast.success("Numerologia eliminata.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16 sm:py-20" data-testid="mie-letture-page">
      <div className="mb-12">
        <p className="eyebrow">Il tuo archivio</p>
        <h1 className="font-display text-4xl text-amber-100 mt-3">Le mie letture</h1>
        <p className="text-purple-200/70 mt-2 text-sm">
          Tutte le letture e le consultazioni fatte fuori dalla chat privata. Quelle della chat protetta dal lucchetto restano ne «La mia memoria».
        </p>
      </div>

      <div data-testid="letture-readings-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Le tue letture
          </h2>
          <Link to="/lettura" data-testid="letture-new-reading-btn" className="text-xs text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 transition-colors">
            Nuova lettura
          </Link>
        </div>
        {readings.length === 0 ? (
          <p className="text-sm text-purple-300/50" data-testid="letture-readings-empty">Le tue letture con le Sibille appariranno qui.</p>
        ) : (
          <div className="space-y-4">
            {readings.map((r) => (
              <details key={r.id} className="mystic-card p-6" data-testid={`reading-card-${r.id}`}>
                <summary className="cursor-pointer list-none">
                  {r.mode === "manual" && (
                    <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-amber-300/70 mb-1" data-testid={`reading-mode-${r.id}`}>Le mie stese</p>
                  )}
                  <p className="text-amber-100 font-medium">“{r.question}”</p>
                  <p className="text-xs text-purple-300/50 mt-1.5">
                    {new Date(r.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })} · {DECK_LABELS[r.deck] || "Sibille"}{r.variant === "cruda" ? " · Variante Cruda" : ""} · {SPREAD_LABELS[r.spread] || `${r.spread} carte`}
                  </p>
                  {r.linked_question && (
                    <p className="text-[11px] text-amber-300/70 mt-1 italic" data-testid={`reading-linked-${r.id}`}>
                      ↳ Collegata a: «{r.linked_question}»
                    </p>
                  )}
                  <p className="text-xs text-purple-300/60 mt-2" data-testid={`reading-cards-${r.id}`}>
                    {r.cards.map((c, i) => (
                      <span key={i}>
                        {i > 0 && " · "}
                        <span title={c.reversed ? "Carta rovesciata" : "Carta dritta"}
                          className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${c.reversed ? "bg-purple-500" : "bg-amber-400"}`} />
                        {c.name}{c.reversed ? " (rovesciata)" : ""}
                      </span>
                    ))}
                  </p>
                </summary>
                <p className="text-sm text-purple-100/80 leading-loose whitespace-pre-line mt-4 pt-4 border-t border-[#4a2175]/50">
                  <MarkdownText text={r.interpretation} />
                </p>
                <ReadingAlerts avvisi={r.avvisi} />
                <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CopyReadingButton text={`“${r.question}”\n${new Date(r.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}\n\n${r.interpretation}`} testid={`copy-reading-${r.id}`} />
                  </div>
                  <button
                    onClick={() => deleteReading(r.id)}
                    data-testid={`reading-delete-${r.id}`}
                    className="flex items-center gap-1.5 text-xs text-red-300/70 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" /> Elimina questa lettura
                  </button>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>

      <div className="mt-14" data-testid="letture-geomancy-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400 flex items-center gap-2">
            <LayoutGrid className="w-4 h-4" /> Le tue geomanzie
          </h2>
          <Link to="/geomanzia" data-testid="letture-new-geomancy-btn" className="text-xs text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 transition-colors">
            Nuova generazione
          </Link>
        </div>
        {geomancies.length === 0 ? (
          <p className="text-sm text-purple-300/50" data-testid="letture-geomancy-empty">Le tue generazioni geomantiche appariranno qui.</p>
        ) : (
          <div className="space-y-3">
            {geomancies.map((g) => (
              <div key={g.id} className="mystic-card px-6 py-4" data-testid={`geomancy-card-${g.id}`}>
                <p className="text-amber-100 font-medium">“{g.question}”</p>
                <p className="text-xs text-purple-300/50 mt-1.5">
                  {new Date(g.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                  {" · "}{g.rows.length}/{g.total_rows} righe
                  {" · "}{g.status === "completed" ? "Completata" : "In corso"}
                </p>
                {g.rows.length > 0 && (
                  <p className="text-xs text-purple-300/60 mt-2">
                    {g.rows.map((r) => `Riga ${r.index}`).join(" · ")}
                  </p>
                )}
                <div className="mt-3 flex items-center gap-4">
                  {g.status === "completed" && (
                    <Link to={`/geomanzia?sessione=${g.id}`} data-testid={`geomancy-open-${g.id}`} className="inline-block text-xs text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 transition-colors">
                      Apri lo scudo
                    </Link>
                  )}
                  <button onClick={() => deleteGeomancy(g.id)} data-testid={`geomancy-delete-${g.id}`} className="flex items-center gap-1.5 text-xs text-red-300/70 hover:text-red-300 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Elimina
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-14" data-testid="letture-numerology-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400 flex items-center gap-2">
            <Hash className="w-4 h-4" /> Le tue numerologie
          </h2>
          <Link to="/numerologia" data-testid="letture-new-numerology-btn" className="text-xs text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 transition-colors">
            Nuova numerologia
          </Link>
        </div>
        {numeros.length === 0 ? (
          <p className="text-sm text-purple-300/50" data-testid="letture-numerology-empty">Le numerologie che salverai appariranno qui.</p>
        ) : (
          <div className="space-y-3">
            {numeros.map((n) => (
              <div key={n.id} className="mystic-card px-6 py-4" data-testid={`numerology-card-${n.id}`}>
                <p className="text-amber-100 font-medium">Data di nascita: {n.birth_date_label}</p>
                <p className="text-xs text-purple-300/50 mt-1.5">
                  Consultata il {new Date(n.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                  {" · "}Percorso di Vita {n.percorso}{n.percorso_master ? " (Numero Maestro)" : ""}
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <Link to={`/numerologia?archivio=${n.id}`} data-testid={`numerology-open-${n.id}`} className="inline-block text-xs text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 transition-colors">
                    Apri la lettura
                  </Link>
                  <button onClick={() => deleteNumerology(n.id)} data-testid={`numerology-delete-${n.id}`} className="flex items-center gap-1.5 text-xs text-red-300/70 hover:text-red-300 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Elimina
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
